import {
  ProgressionModel,
  ProgressionSourceWithModels,
} from 'features/wildfires/wildfire.types';
import { useProgressionModels } from '../useProgressionModels';
import { useProgressionModelsSources } from '../useProgressionModelsSources';

type UseProgressionSourcesWithModelsProps = {
  wildfireId?: number;
};

type UseProgressionSourcesWithModels = {
  progressionSourcesWithModels: ProgressionSourceWithModels[];
  isLoading: boolean;
  error: Error | null;
};

const modelListSortFunction = (
  a: ProgressionModel | undefined,
  b: ProgressionModel | undefined,
): number => {
  // Handle undefined cases - sort undefined values to the end
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  // Sort by source reference time zero descending (most recent first)
  return (
    new Date(b.sourceReferenceTimeZero).getTime() -
    new Date(a.sourceReferenceTimeZero).getTime()
  );
};

const modelGroupsSortFunction = (
  a: ProgressionSourceWithModels,
  b: ProgressionSourceWithModels,
): number => {
  // Convert boolean to number (1 if has model, 0 if not)
  const aHasModel = Number(Boolean(a.models.length));
  const bHasModel = Number(Boolean(b.models.length));

  // Sort by model presence (items with model come first) and then by the first model's source reference time zero
  return (
    bHasModel - aHasModel || modelListSortFunction(a.models[0], b.models[0])
  );
};

export const useProgressionSourcesWithModels = (
  props: UseProgressionSourcesWithModelsProps,
): UseProgressionSourcesWithModels => {
  const { wildfireId } = props;

  const {
    progressionModelsSources,
    isLoading: isLoadingSources,
    error: sourcesError,
  } = useProgressionModelsSources();

  const {
    progressionModels,
    isLoading: isLoadingModels,
    error: modelsError,
  } = useProgressionModels({ wildfireId });

  const progressionSourcesWithModels: ProgressionSourceWithModels[] =
    progressionModelsSources
      .map((source) => {
        const models = progressionModels
          .filter((m) => m.sourceAttribution === source.sourceAttribution)
          .sort(modelListSortFunction);
        return { ...source, models };
      })
      .sort(modelGroupsSortFunction);

  return {
    progressionSourcesWithModels,
    isLoading: isLoadingSources || isLoadingModels,
    error: sourcesError || modelsError,
  };
};
